package org.teddapp.project.ui.skills

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.tierColor

/** Fraction de la barre réservée au nom : assez pour lire, pas au point d'écraser la mesure. */
private const val NAME_WEIGHT = 0.42f

/**
 * Vue d'ensemble des compétences : une barre par compétence, heures faites contre
 * cible, la plus avancée en premier.
 *
 * C'est la seule lecture qui répond d'un coup d'œil à « où j'en suis » quand il y a
 * trente entrées. Une liste de cartes demande de faire la comparaison de tête ; des
 * barres alignées sur la même échelle la font voir.
 *
 * Les compétences sans une seule heure sont montrées quand même, en bas et sans
 * barre. Leur absence de progression est une information : ce sont des intentions,
 * pas encore des compétences.
 */
@Composable
internal fun SkillsOverview(model: SkillsModel, onSelect: (String) -> Unit, modifier: Modifier = Modifier) {
    val lignes = model.visible.sortedWith(
        compareByDescending<SkillStats> { it.minutes }.thenBy { it.skill.name },
    )
    if (lignes.isEmpty()) return

    val commencees = lignes.filter { it.minutes > 0 }
    val declarees = lignes.filter { it.minutes == 0 }
    val totalHeures = lignes.sumOf { it.minutes } / 60.0

    Column(modifier = modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(22.dp)) {
        OverviewSummary(
            total = totalHeures,
            commencees = commencees.size,
            declarees = declarees.size,
            archivees = model.archivedCount,
        )

        if (commencees.isNotEmpty()) {
            // L'échelle est commune : une barre pleine, c'est la compétence la plus
            // travaillée. Comparer à la cible de chacune viendrait ensuite.
            val plafond = commencees.maxOf { it.minutes }.coerceAtLeast(1)
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                Text("TEMPS INVESTI", style = TeddType.Overline, color = TeddTokens.InkMuted)
                commencees.forEach { st -> OverviewBar(st, plafond, model, onSelect) }
            }
        }

        if (declarees.isNotEmpty()) {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                Text(
                    text = "DÉCLARÉES, PAS ENCORE COMMENCÉES · ${declarees.size}",
                    style = TeddType.Overline,
                    color = TeddTokens.InkMuted,
                )
                Text(
                    text = "Aucune heure n'y est rattachée. Une course de focus compte pour une " +
                        "compétence quand la tâche, l'objectif ou le projet lui est rattaché.",
                    color = TeddTokens.InkMuted,
                    style = TeddType.BodyLg,
                    fontSize = 13.sp,
                )
                declarees.forEach { st -> OverviewEmptyRow(st, model, onSelect) }
            }
        }
    }
}

/** Trois nombres qui situent l'ensemble avant d'entrer dans le détail. */
@Composable
private fun OverviewSummary(total: Double, commencees: Int, declarees: Int, archivees: Int) {
    Row(horizontalArrangement = Arrangement.spacedBy(40.dp)) {
        SummaryNumber("heures investies", formatHours((total * 60).toInt()))
        SummaryNumber("commencées", commencees.toString())
        SummaryNumber("déclarées", declarees.toString())
        if (archivees > 0) SummaryNumber("archivées", archivees.toString())
    }
}

@Composable
private fun SummaryNumber(label: String, value: String) {
    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
        Text(
            text = value,
            style = TeddType.Numeral.copy(fontSize = 26.sp, lineHeight = 30.sp),
            color = TeddTokens.Ink,
        )
        Text(label.uppercase(), style = TeddType.Overline, color = TeddTokens.InkMuted)
    }
}

/** Une compétence commencée : son nom, sa barre, ses heures. */
@Composable
private fun OverviewBar(st: SkillStats, plafond: Int, model: SkillsModel, onSelect: (String) -> Unit) {
    val accent = st.tier?.let { tierColor(it) } ?: TeddTokens.LimeStrong
    val part = (st.minutes.toFloat() / plafond).coerceIn(0f, 1f)

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(TeddTokens.RSm))
            .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) {
                onSelect(st.skill.id)
            }
            .padding(vertical = 4.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            text = st.skill.name,
            color = TeddTokens.Ink,
            fontSize = 13.sp,
            fontWeight = FontWeight.Medium,
            maxLines = 1,
            modifier = Modifier.weight(NAME_WEIGHT).padding(end = 14.dp),
        )
        Box(
            modifier = Modifier
                .weight(1f - NAME_WEIGHT)
                .height(14.dp)
                .clip(RoundedCornerShape(TeddTokens.RXs))
                .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RXs)),
        ) {
            Box(
                modifier = Modifier
                    .fillMaxWidth(part)
                    .height(14.dp)
                    .clip(RoundedCornerShape(TeddTokens.RXs))
                    .background(accent, RoundedCornerShape(TeddTokens.RXs)),
            )
        }
        Text(
            text = formatHours(st.minutes),
            style = TeddType.Mono,
            color = TeddTokens.InkMuted,
            fontSize = 11.5.sp,
            modifier = Modifier.width(72.dp).padding(start = 12.dp),
        )
    }
}

/** Une compétence déclarée : pas de barre, seulement sa cible. */
@Composable
private fun OverviewEmptyRow(st: SkillStats, model: SkillsModel, onSelect: (String) -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(TeddTokens.RSm))
            .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) {
                onSelect(st.skill.id)
            }
            .padding(vertical = 3.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            text = st.skill.name,
            color = TeddTokens.InkMuted,
            fontSize = 12.5.sp,
            maxLines = 1,
            modifier = Modifier.weight(1f).padding(end = 12.dp),
        )
        val etiquettes = model.tagsOf(st.skill)
        if (etiquettes.isNotEmpty()) {
            Text(
                text = etiquettes.joinToString(" · "),
                color = TeddTokens.InkSubtle,
                fontSize = 11.sp,
                modifier = Modifier.padding(end = 12.dp),
            )
        }
        Text(
            text = "cible ${st.skill.target_hours} h",
            style = TeddType.Mono,
            color = TeddTokens.InkSubtle,
            fontSize = 11.sp,
        )
        SkillDeadline.label(st.skill.deadline)?.let {
            Text(" · ", style = TeddType.Mono, color = TeddTokens.InkSubtle, fontSize = 11.sp)
            DeadlineText(it)
        }
    }
}
